import { useState, useMemo } from 'react';
import { useCatalog, CatalogItem } from '../hooks/useCatalog';
import { useAccountsCustomers } from '../hooks/useParties';
import { useCreateSalesInvoice } from '../hooks/useTransactions';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { PlusCircle, Trash2, Search } from 'lucide-react';
import { format } from 'date-fns';

/** Default due date: the last day of the invoice date's month (client, 2026-11-03). */
function monthEnd(day: string): string {
  const [y, m] = day.split('-').map(Number);
  if (!y || !m) return day;
  return format(new Date(y, m, 0), 'yyyy-MM-dd'); // day 0 of next month
}
import { api } from '@/shared/api/axios';
import { formatINR } from '@/shared/utils/currency';

interface LineItem {
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  discount: number;
  cgstRate: number;
  sgstRate: number;
  igstRate: number;
}

const EMPTY_LINE: LineItem = { description: '', hsnSac: '', quantity: 1, rate: 0, discount: 0, cgstRate: 0, sgstRate: 0, igstRate: 0 };

interface Props {
  onSuccess: () => void;
  onCancel: () => void;
  prefillData?: { editingId?: string; __v?: number; customerId?: string; date?: string; dueDate?: string; lineItems?: LineItem[]; paymentTerms?: string; terms?: string; notes?: string; roundOff?: number };
}

export function InvoiceGenerator({ onSuccess, onCancel, prefillData }: Props) {
  const { data: customers } = useAccountsCustomers();
  const { data: catalogItems } = useCatalog();
  const createInvoice = useCreateSalesInvoice();

  const [customerId, setCustomerId] = useState(prefillData?.customerId || '');
  const [date, setDate] = useState(() => prefillData?.date?.slice(0, 10) || format(new Date(), 'yyyy-MM-dd'));
  const [dueDate, setDueDate] = useState(() => prefillData?.dueDate?.slice(0, 10) || monthEnd(prefillData?.date?.slice(0, 10) || format(new Date(), 'yyyy-MM-dd')));
  // Until someone picks a due date themselves, it follows the invoice date's month end.
  const [dueTouched, setDueTouched] = useState(() => Boolean(prefillData?.dueDate && prefillData?.date && prefillData.dueDate.slice(0, 10) !== monthEnd(prefillData.date.slice(0, 10))));
  const [paymentTerms, setPaymentTerms] = useState(prefillData?.paymentTerms || 'Net 30');
  const [lineItems, setLineItems] = useState<LineItem[]>(prefillData?.lineItems || [{ ...EMPTY_LINE }]);
  const [notes, setNotes] = useState(prefillData?.notes || '');
  const [terms, setTerms] = useState(prefillData?.terms || '');
  const [catalogSearch, setCatalogSearch] = useState('');
  const [activeLineIdx, setActiveLineIdx] = useState<number | null>(null);

  // Compute totals
  const computations = useMemo(() => {
    let subTotal = 0, cgstTotal = 0, sgstTotal = 0, igstTotal = 0;
    for (const li of lineItems) {
      const lineAmt = (li.quantity * li.rate) - li.discount;
      subTotal += lineAmt;
      cgstTotal += Math.round(lineAmt * (li.cgstRate / 100) * 100) / 100;
      sgstTotal += Math.round(lineAmt * (li.sgstRate / 100) * 100) / 100;
      igstTotal += Math.round(lineAmt * (li.igstRate / 100) * 100) / 100;
    }
    const grandTotal = subTotal + cgstTotal + sgstTotal + igstTotal;
    const suggestedRoundOff = Math.round((Math.round(grandTotal) - grandTotal) * 100) / 100;
    const roundOff = Math.abs(suggestedRoundOff) <= 1 ? suggestedRoundOff : 0;
    const netTotal = Math.round((grandTotal + roundOff) * 100) / 100;
    return { subTotal: Math.round(subTotal * 100) / 100, cgstTotal, sgstTotal, igstTotal, grandTotal, roundOff, netTotal };
  }, [lineItems]);

  const roundOff = computations.roundOff;

  const updateLine = (idx: number, field: keyof LineItem, value: any) => {
    setLineItems(prev => prev.map((li, i) => i === idx ? { ...li, [field]: value } : li));
  };

  const addLine = () => setLineItems(prev => [...prev, { ...EMPTY_LINE }]);
  const removeLine = (idx: number) => { if (lineItems.length > 1) setLineItems(prev => prev.filter((_, i) => i !== idx)); };

  const selectCatalogItem = (item: CatalogItem, lineIdx: number) => {
    setLineItems(prev => prev.map((li, i) => i === lineIdx ? {
      ...li, description: item.name, hsnSac: item.hsnSac, rate: item.defaultRate,
      discount: item.defaultDiscount, cgstRate: item.cgstRate, sgstRate: item.sgstRate, igstRate: item.igstRate,
    } : li));
    setCatalogSearch('');
    setActiveLineIdx(null);
  };

  const filteredCatalog = catalogSearch.length >= 1
    ? (catalogItems || []).filter(c => c.name.toLowerCase().includes(catalogSearch.toLowerCase()) || c.sku.toLowerCase().includes(catalogSearch.toLowerCase()))
    : [];

  const handleGenerate = async () => {
    if (!customerId) return alert('Please select a customer.');
    if (lineItems.some(li => !li.description || li.rate <= 0)) return alert('All line items need a description and rate > 0.');

    const payload = {
      date, dueDate, customerId, lineItems, paymentTerms, roundOff,
      notes, terms, __v: prefillData?.__v,
    };
    if (prefillData?.editingId) await api.put(`/api/accounts/sales-invoices/${prefillData.editingId}`, payload);
    else await createInvoice.mutateAsync(payload);
    onSuccess();
  };

  const customerOptions = [{ value: '', label: '— Select Customer —' }, ...(customers || []).map(c => ({ value: c._id, label: `${c.name}${c.gstNumber ? ` (${c.gstNumber})` : ''}` }))];

  return (
    <Card className="shadow-xs">
      <CardHeader title={prefillData?.editingId ? 'Edit Draft Sales Invoice' : 'Generate Sales Invoice'} action={
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={handleGenerate} disabled={createInvoice.isPending}>
            {createInvoice.isPending ? 'Generating...' : 'Generate Invoice'}
          </Button>
        </div>
      } />
      <CardBody className="space-y-6">
        {/* Header Fields */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="col-span-2">
            <label className="text-sm font-medium">Customer *</label>
            <Select name="customerId" value={customerId} onChange={e => setCustomerId(e.target.value)} options={customerOptions} />
          </div>
          <div>
            <label className="text-sm font-medium">Invoice Date *</label>
            <Input name="date" type="date" value={date} onChange={e => { setDate(e.target.value); if (!dueTouched && e.target.value) setDueDate(monthEnd(e.target.value)); }} />
          </div>
          <div>
            <label className="text-sm font-medium">Due Date *</label>
            <Input name="dueDate" type="date" value={dueDate} onChange={e => { setDueDate(e.target.value); setDueTouched(true); }} />
          </div>
          <div>
            <label className="text-sm font-medium">Payment Terms</label>
            <Select name="paymentTerms" value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} options={[
              { value: 'Net 7', label: 'Net 7' }, { value: 'Net 15', label: 'Net 15' }, { value: 'Net 30', label: 'Net 30' },
              { value: 'Net 45', label: 'Net 45' }, { value: 'Net 60', label: 'Net 60' }, { value: 'Due on Receipt', label: 'Due on Receipt' },
            ]} />
          </div>
        </div>

        {/* Line Items */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold">Line Items</h3>
            <Button variant="ghost" size="sm" onClick={addLine}><PlusCircle className="h-4 w-4 mr-1" /> Add Line</Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-label-caps uppercase text-on-surface-variant">
                <tr>
                  <th className="px-3 py-2.5 text-left w-[30%]">Description</th>
                  <th className="px-3 py-2.5 w-[8%]">HSN/SAC</th>
                  <th className="px-3 py-2.5 w-[7%] text-right">Qty</th>
                  <th className="px-3 py-2.5 w-[10%] text-right">Rate</th>
                  <th className="px-3 py-2.5 w-[8%] text-right">Disc.</th>
                  <th className="px-3 py-2.5 w-[7%] text-right">CGST%</th>
                  <th className="px-3 py-2.5 w-[7%] text-right">SGST%</th>
                  <th className="px-3 py-2.5 w-[7%] text-right">IGST%</th>
                  <th className="px-3 py-2.5 w-[10%] text-right">Amount</th>
                  <th className="px-3 py-2.5 w-[5%]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lineItems.map((li, idx) => {
                  const lineAmt = (li.quantity * li.rate) - li.discount;
                  return (
                    <tr key={idx} className="group">
                      <td className="px-3 py-2.5 relative">
                        <div className="flex items-center gap-1">
                          <Input name={`desc-${idx}`} value={li.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                            onFocus={() => setActiveLineIdx(idx)} placeholder="Type or search catalog..." className="text-sm" />
                          <button onClick={() => { setActiveLineIdx(idx); setCatalogSearch(li.description); }} className="rounded p-1 hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring" aria-label={`Search catalog for line ${idx + 1}`} title="Search catalog">
                            <Search className="h-3.5 w-3.5 text-muted-foreground" />
                          </button>
                        </div>
                        {activeLineIdx === idx && filteredCatalog.length > 0 && (
                          <div className="absolute z-50 top-full left-0 right-0 mt-1 max-h-40 overflow-y-auto rounded-card-sm border border-border bg-popover shadow-surface-2">
                            {filteredCatalog.slice(0, 8).map(item => (
                              <button key={item._id} onClick={() => selectCatalogItem(item, idx)}
                                className="w-full px-3 py-2 text-left hover:bg-muted/50 flex justify-between items-center text-sm">
                                <span>{item.name} {item.hsnSac && <span className="text-xs text-muted-foreground">({item.hsnSac})</span>}</span>
                                <span className="text-xs font-medium font-mono tabular-nums">{formatINR(item.defaultRate)}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5"><Input name={`hsn-${idx}`} value={li.hsnSac} onChange={e => updateLine(idx, 'hsnSac', e.target.value)} className="text-xs" /></td>
                      <td className="px-3 py-2.5"><Input name={`qty-${idx}`} type="number" min={1} value={li.quantity} onChange={e => updateLine(idx, 'quantity', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5"><Input name={`rate-${idx}`} type="number" value={li.rate} onChange={e => updateLine(idx, 'rate', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5"><Input name={`disc-${idx}`} type="number" value={li.discount} onChange={e => updateLine(idx, 'discount', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5"><Input name={`cgst-${idx}`} type="number" value={li.cgstRate} onChange={e => updateLine(idx, 'cgstRate', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5"><Input name={`sgst-${idx}`} type="number" value={li.sgstRate} onChange={e => updateLine(idx, 'sgstRate', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5"><Input name={`igst-${idx}`} type="number" value={li.igstRate} onChange={e => updateLine(idx, 'igstRate', Number(e.target.value))} className="text-sm text-right font-mono tabular-nums" /></td>
                      <td className="px-3 py-2.5 text-right font-mono tabular-nums font-medium text-on-surface">{formatINR(lineAmt)}</td>
                      <td className="px-3 py-2.5"><button onClick={() => removeLine(idx)} className="opacity-0 group-hover:opacity-100 text-destructive" aria-label={`Remove line ${idx + 1}`} title="Remove line"><Trash2 className="h-4 w-4" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Totals — the printed-invoice convention: subtle labels left, mono figures right. */}
        <div className="flex justify-end">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-on-surface-variant">Sub Total</span><span className="font-mono tabular-nums font-medium">{formatINR(computations.subTotal)}</span></div>
            {computations.cgstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>CGST</span><span className="font-mono tabular-nums">{formatINR(computations.cgstTotal)}</span></div>}
            {computations.sgstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>SGST</span><span className="font-mono tabular-nums">{formatINR(computations.sgstTotal)}</span></div>}
            {computations.igstTotal > 0 && <div className="flex justify-between text-muted-foreground"><span>IGST</span><span className="font-mono tabular-nums">{formatINR(computations.igstTotal)}</span></div>}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Round Off</span>
              <Input
                name="roundOff"
                type="number"
                step="any"
                value={computations.roundOff || 0}
                readOnly
                className="w-28 cursor-not-allowed text-right font-mono tabular-nums text-sm"
                placeholder="0.00"
              />
            </div>
            <div className="mt-1 flex justify-between items-center border-t border-border pt-2">
              <span className="text-label-sm uppercase tracking-wider text-on-surface-variant">Net Total</span>
              <span className="font-mono tabular-nums text-data-lg font-semibold text-primary">{formatINR(computations.netTotal)}</span>
            </div>
          </div>
        </div>

        {/* Notes & Terms */}
        <div className="grid grid-cols-2 gap-4">
          <div><label className="text-sm font-medium">Notes</label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full rounded-md border px-3 py-2 text-sm" placeholder="Visible on invoice" /></div>
          <div><label className="text-sm font-medium">Terms & Conditions</label><textarea value={terms} onChange={e => setTerms(e.target.value)} rows={3} className="w-full rounded-md border px-3 py-2 text-sm" placeholder="Payment terms, warranty, etc." /></div>
        </div>
      </CardBody>
    </Card>
  );
}
